import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase.config';
import '../../styles/kyro-design.css';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  photoURL?: string;
  order: number;
  creds?: string[];
  email?: string;
  linkedin?: string;
  previouslyAt?: string[];
  education?: string[];
  showOnWebsite?: boolean;
}

const getInitials = (name: string): string => {
  return name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
};

const principles = [
  { icon: '⚖', t: 'Stewardship', d: 'Capital is a responsibility. We manage it with the discipline of long-term ownership.' },
  { icon: '🔬', t: 'Discipline', d: 'Research-led. Valuation-driven. Every decision grounded in rigorous analysis.' },
  { icon: '🤝', t: 'Integrity', d: 'Trust compounds over time. We act transparently with every investor and partner.' },
  { icon: '🎯', t: 'Alignment', d: 'Long-term partnerships. Shared outcomes. Our capital is invested alongside yours.' },
];

const About: React.FC = () => {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const q = query(collection(db, 'teamMembers'), orderBy('order'));
        const snap = await getDocs(q);
        setTeam(
          snap.docs
            .map((d) => {
              const data = d.data();
              const creds = Array.isArray(data.creds)
                ? data.creds
                : data.education && Array.isArray(data.education)
                  ? data.education
                  : [];
              return { id: d.id, ...data, creds } as TeamMember;
            })
            .filter((m) => m.showOnWebsite !== false)
        );
      } catch {
        setTeam([]);
      } finally {
        setLoading(false);
      }
    };
    loadTeam();
  }, []);

  return (
    <div className="page">
      <div className="hero" style={{ minHeight: '48vh', paddingBottom: '4rem' }}>
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner">
          <div className="kicker">
            <div className="kicker-dot" />
            <div className="kicker-line" />
            About Us
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>
            Capital. Discipline. Trust.<br />
            <span className="dim">An Institutional Capital & Advisory Group.</span>
          </h1>
          <p className="hero-sub">
            Kyro is an integrated financial services group operating across asset management and strategic corporate advisory — built for the long term.
          </p>
        </div>
      </div>

      <div className="sec">
        <div className="grid-2">
          <div>
            <div className="eyebrow">Who We Are</div>
            <div className="sec-title">
              Enduring Capital.<br />Institutional Discipline.
            </div>
            <p className="sec-body" style={{ marginBottom: '1rem' }}>
              Kyro is an integrated financial services institution operating across asset management and strategic corporate advisory. We allocate capital selectively, advise strategically, and build for the long term.
            </p>
            <p className="sec-body">
              Our mandate is simple: preserve trust, deploy capital responsibly, and create enduring value — for investors, founders and institutions we serve.
            </p>
          </div>
          <div>
            <div className="eyebrow">Our Approach</div>
            <div className="sec-title">A Disciplined Allocation Framework.</div>
            <p className="sec-body" style={{ marginBottom: '1rem' }}>
              We pursue a multi-cap, opportunity-driven investment strategy across private and public markets. Capital is deployed across growth-stage, pre-listing and listed equity opportunities — dynamically allocated, valuation-conscious and cycle-aware.
            </p>
            <p className="sec-body">
              We are not dependent on issuance cycles. We are guided by discipline, research and governance standards.
            </p>
          </div>
        </div>
      </div>

      <div className="sec-off">
        <div className="eyebrow">Our Businesses</div>
        <div className="sec-title">Two Complementary Platforms.</div>
        <div className="biz-grid">
          <div className="biz-card">
            <div className="biz-card-head">
              <div className="biz-card-left">
                <div className="biz-type">Asset Management</div>
                <div className="biz-name">Kyro Asset Management</div>
                <div className="biz-sub">Investment Manager — SEBI-Registered Category II AIF (Reg. No. IN/AIF2/26-27/2191)</div>
              </div>
              <div className="biz-card-logo">
                <img src="/assets/logos/kyro-asset-management-logo.png" alt="Kyro Asset Management" />
              </div>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--body)', lineHeight: 1.75, marginBottom: '1rem' }}>
              The group's asset management arm. Investment Manager to a SEBI-registered Category II AIF. Strategy encompasses private and public equity opportunities across market cycles.
            </p>
            <ul className="biz-pts">
              {['Growth-oriented private investments', 'Primary issuance and IPO participation', 'Structured and strategic capital solutions', 'Secondary market allocations', 'High-conviction public equity exposure'].map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
          <div className="biz-card">
            <div className="biz-card-head">
              <div className="biz-card-left">
                <div className="biz-type">Corporate Advisory</div>
                <div className="biz-name">Kyro Capital</div>
                <div className="biz-sub">Capital Strategy & Institutional Execution</div>
              </div>
              <div className="biz-card-logo">
                <img src="/assets/logos/kyro-capital-logo.png" alt="Kyro Capital" />
              </div>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--body)', lineHeight: 1.75, marginBottom: '1rem' }}>
              The group's corporate advisory arm. Advises companies on capital formation, public market readiness and structured financing — working alongside management teams with discipline and clarity.
            </p>
            <ul className="biz-pts">
              {['IPO preparedness and listing strategy', 'Private placements and capital structuring', 'Investor relations and strategic communications', 'Debt and structured capital advisory', 'Family office advisory'].map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="eyebrow">Our Principles</div>
        <div className="sec-title">What We Stand For.</div>
        <div className="prin-grid">
          {principles.map((p) => (
            <div className="prin" key={p.t}>
              <div className="prin-icon">{p.icon}</div>
              <div className="prin-title">{p.t}</div>
              <div className="prin-desc">{p.d}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="sec-off">
        <div className="eyebrow">Investment Committee & Team</div>
        <div className="sec-title">Our People.</div>
        <p className="sec-body" style={{ maxWidth: 540, marginBottom: '1.5rem' }}>
          Our investment team includes experienced professionals responsible for originating, executing, monitoring and exiting transactions across our verticals.
        </p>
        <div className="team-grid">
          {loading ? (
            <p style={{ color: 'var(--gray)', textAlign: 'center', gridColumn: '1 / -1' }}>Loading team...</p>
          ) : team.length === 0 ? (
            <p style={{ color: 'var(--gray)', textAlign: 'center', gridColumn: '1 / -1' }}>No team members yet. Add them in the Admin Dashboard.</p>
          ) : (
            team.map((t) => (
              <div className="team-card" key={t.id}>
                <div className="team-head">
                  {t.photoURL ? (
                    <img src={t.photoURL} alt={t.name} className="team-avatar-img" />
                  ) : (
                    <div className="avatar">{getInitials(t.name)}</div>
                  )}
                  <div>
                    <div className="t-name">{t.name}</div>
                    <div className="t-role">{t.role}</div>
                  </div>
                </div>
                <div className="team-body">
                  <div className="team-bio">{t.bio}</div>
                  {(() => {
                    const tags = Array.isArray(t.creds) ? t.creds : (Array.isArray(t.education) ? t.education : []);
                    return tags.length > 0 ? (
                      <div className="creds">
                        {tags.map((c) => (
                          <span className="cred" key={String(c)}>{String(c)}</span>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  {(t.email || t.linkedin) && (
                    <div className="team-links" style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {t.email && <a href={`mailto:${t.email}`} style={{ fontSize: '0.8rem', color: 'var(--blue)' }}>✉ {t.email}</a>}
                      {t.linkedin && <a href={t.linkedin} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: 'var(--blue)' }}>LinkedIn ↗</a>}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default About;
